'use client';

import { withBase } from '../lib/brand';
import React from 'react';
import Link from 'next/link';
import {
  driveawayBookingUrl,
  trackHireClick,
  type HirePlacement,
  type HirePrefill,
} from '../lib/driveaway';

/**
 * Outbound booking button — the ONLY place users are handed to
 * DriveAway (white-label, Camping Australia branded). Everything
 * before this click stays on our site.
 */
export function HireBookButton({
  placement,
  children,
  variant = 'primary',
  className = '',
}: {
  placement: HirePlacement;
  children: React.ReactNode;
  variant?: 'primary' | 'ghost';
  className?: string;
}) {
  const base =
    variant === 'primary'
      ? 'inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:opacity-90 active:scale-[0.99]'
      : 'inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-semibold border border-emerald-700 text-emerald-800 transition hover:bg-emerald-50 active:scale-[0.99]';
  return (
    <a
      href={driveawayBookingUrl(placement)}
      target="_blank"
      rel="noopener noreferrer sponsored"
      onClick={() => trackHireClick(placement)}
      className={`${base} ${className}`}
      style={variant === 'primary' ? { backgroundColor: '#047857' } : undefined}
    >
      {children}
      <svg
        className="h-4 w-4"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <path d="M7 17 17 7M7 7h10v10" />
      </svg>
    </a>
  );
}

/**
 * Compact card for the trip planner sidebar — shown once a route is
 * planned. Links to our own /campervan-hire content page first;
 * the direct booking handoff is the secondary action.
 */
export function TripHireCard({ prefill }: { prefill?: HirePrefill }) {
  const bookingUrl = driveawayBookingUrl('trip-planner-sidebar', prefill);
  const prefilled = !!prefill?.pickupCity;
  return (
    <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50/60 p-4">
      <div className="flex items-start gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-emerald-700/10 text-emerald-800">
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <path d="M3 17h1m16 0h1M6 17a2 2 0 1 0 4 0m4 0a2 2 0 1 0 4 0M4 17V7a1 1 0 0 1 1-1h10l4 5h1a1 1 0 0 1 1 1v5" />
            <path d="M15 6v5h4" />
          </svg>
        </span>
        <div className="min-w-0">
          <h4 className="text-sm font-semibold text-slate-900">
            Need wheels for this trip?
          </h4>
          <p className="mt-0.5 text-xs text-slate-600 leading-relaxed">
            {prefilled
              ? `Hire a campervan or motorhome from ${prefill!.pickupCity} for these dates — pick up here, drop off anywhere.`
              : 'Hire a campervan, motorhome or 4WD camper — pick up in one city, drop off in another.'}
          </p>
          <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1.5">
            <Link
              href={withBase("/campervan-hire")}
              className="text-xs font-semibold text-emerald-800 hover:text-emerald-900 underline underline-offset-2"
            >
              Compare vehicles
            </Link>
            <a
              href={bookingUrl}
              target="_blank"
              rel="noopener noreferrer sponsored"
              onClick={() => trackHireClick('trip-planner-sidebar')}
              className="text-xs font-semibold text-emerald-800 hover:text-emerald-900"
            >
              {prefilled ? 'Check prices for these dates →' : 'Check prices & availability →'}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}


/**
 * Campsite detail sidebar cross-sell — photo-led, same visual language as
 * VanHirePromo on the results list (owned Unsplash-licensed photography,
 * bottom scrim, white pill CTA) but with the bush-camp rooftop-tent shot so
 * the two placements read as siblings, not clones. Whole card is one link
 * into the hire guide (live search + DriveAway handoff live there).
 */
export function CampsiteHireCard({ town, state }: { town?: string; state?: string }) {
  const place = [town, state].filter(Boolean).join(', ');
  const trackPromo = () => {
    trackHireClick('campsite-detail-sidebar');
    const w = window as unknown as { gtag?: (...args: unknown[]) => void };
    w.gtag?.('event', 'select_promotion', {
      creative_name: 'van_hire_card_photo',
      creative_slot: 'campsite_detail_sidebar',
      promotion_id: 'campervan_hire',
      promotion_name: 'Campervan & 4WD hire',
    });
  };
  return (
    <Link
      href={withBase("/campervan-hire")}
      onClick={trackPromo}
      className="group relative block overflow-hidden rounded-2xl shadow-sm ring-1 ring-black/5 transition-shadow duration-300 hover:shadow-lg"
      aria-label="Campervan and 4WD hire — compare vehicles and live prices"
    >
      <div className="relative h-52 w-full">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={withBase("/images/hire/rooftop-tent-bush.jpg")}
          alt="A 4WD with a rooftop tent set up at a eucalyptus bush camp"
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
          // Lazy: this card sits in the campsite sidebar, below the fold on a
          // phone. Eager, it competed with the hero photo for bandwidth on
          // every campsite page (mobile LCP > 2.5s on 3,916 URLs, Search
          // Console 5 Oct 2026).
          loading="lazy"
          decoding="async"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(180deg, rgba(28,30,32,0) 12%, rgba(28,30,32,0.30) 44%, rgba(28,30,32,0.68) 72%, rgba(28,30,32,0.93) 100%)',
          }}
        />
      </div>
      <div
        className="absolute inset-x-0 bottom-0 p-4 text-white"
        style={{ textShadow: '0 1px 3px rgba(0,0,0,0.5), 0 2px 14px rgba(0,0,0,0.45)' }}
      >
        <div className="mb-1.5 flex items-center gap-2">
          <span className="h-px w-6 bg-white/90" />
          <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-white">
            Campervan &amp; 4WD hire
          </span>
        </div>
        <h3
          className="text-lg leading-tight text-white"
          style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 600 }}
        >
          Touring without your own rig?
        </h3>
        <p className="mt-1 max-w-[34ch] text-[13px] font-medium leading-snug text-white/95">
          {place
            ? `Hire one and make ${place} a stop on the lap — live prices, one-way hires.`
            : 'Live prices across the major rental brands — one-way hires included.'}
        </p>
        <span
          className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-white px-3.5 py-1.5 text-[13px] font-semibold text-[#1c1e20] shadow-sm transition-colors duration-300 group-hover:bg-[#1a68d1] group-hover:text-white"
          style={{ textShadow: 'none' }}
        >
          Compare &amp; book
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
            className="transition-transform duration-300 group-hover:translate-x-0.5" aria-hidden="true">
            <path d="M5 12h14M13 6l6 6-6 6" />
          </svg>
        </span>
      </div>
    </Link>
  );
}
